import {
  useLocale,
  useNotification,
  useNotificationConfiguration,
  useResolverEffect,
  useTest,
  useThemeName,
  useTimeZone,
  useUserId
} from '@enisco/mcc-react-lib'
import {modules} from '../util/testData'
import {Profile} from '../model/profiles'
import {
  setDefaultParameters,
  setFavoriteApps,
  setFavoriteGroups,
  setProfile,
  setRegisteredApps,
  setRegisteredModules,
  setRegisteredUserSettings
} from '../store/appsSlice'
import {App, Module} from '../model/apps'
import {Favorite, Group} from '../model/favorites'
import {useDispatch} from 'react-redux'
import {UserSetting} from '../model/userSetting'
import {useIntl} from 'react-intl'

/**
 * Loads the initial portal config from the API
 */
export const useConfiguration = () => {
  const intl = useIntl()
  const sendNotification = useNotification()
  const dispatch = useDispatch()
  const uid = useUserId()
  const [,setNotificationConfiguration] = useNotificationConfiguration()
  const [, setThemeName] = useThemeName()
  const [, setLocale] = useLocale()
  const [, setTimezone] = useTimeZone()
  const test = useTest()
  const [, loadingConfiguration] = useResolverEffect(
    'mcc-portal-api',
    '/internal/get-config',
    {
      options: {
        cache: 'no-cache'
      },
      mockedData: test ? JSON.stringify(modules) : undefined,
      resolveCondition: [uid],
      onSuccess: (response) =>
        response.json().then((payload) => {
          const appsLoaded = payload.apps || []
          const modulesLoaded = payload.modules || []
          const favoritesLoaded = payload.favorites || []
          const groupsLoaded = payload.groups || []
          const defaultParametersLoaded = payload.defaultParameters || []
          const profileConfig: Profile = payload.profile || {}
          if (!profileConfig.user) {
            profileConfig.user = uid
            profileConfig.id = undefined
          }

          if (profileConfig.notifications) {
            setNotificationConfiguration(profileConfig.notifications)
          }
          dispatch(setRegisteredApps(appsLoaded))
          dispatch(setDefaultParameters(defaultParametersLoaded))
          modulesLoaded.forEach((module: Module) => {
            module.apps = appsLoaded.filter(
              (app: App) => app.module === module.name
            )
          })

          dispatch(setRegisteredModules(modulesLoaded))
          dispatch(setFavoriteApps(favoritesLoaded))
          groupsLoaded.forEach((group: Group) => {
            group.favorites = favoritesLoaded.filter(
              (fav: Favorite) => fav.groupId === group.id
            )
          })
          dispatch(setFavoriteGroups(groupsLoaded))
          dispatch(setProfile(profileConfig))
          if (profileConfig.theme) {
            setThemeName(profileConfig.theme)
          }

          if (profileConfig.locale) {
            setLocale(profileConfig.locale)
          }

          if (profileConfig.timeZone) {
            setTimezone(profileConfig.timeZone)
          }
        })
    }
  )
  const [,loadingUserSettings] = useResolverEffect('mcc-portal-api', '/internal/user-settings', {
    options: {
      method: 'GET',
      headers: {'Content-Type': 'application/json'},
      cache: 'no-cache'
    },
    resolveCondition: [uid],
    onSuccess: async (data) => {
      const json = await data.json()
      const settings = (json.userSettings as UserSetting[]).sort((a, b) => {
        return a.key.localeCompare(b.key)
      })
      dispatch(setRegisteredUserSettings(settings))
    },
    onError: () =>
      sendNotification(
        'warning',
        intl.formatMessage({
          id: 'UserSettingManifests.FetchFailed',
          defaultMessage: 'Could not fetch user settings'
        })
      )
  })
  return loadingConfiguration || loadingUserSettings
}